// Shared app shell: Sidebar (left nav) + TopBar (command + actions)
// Used across every screen so the system feels unified.

import { useAuth } from '../src/lib/auth.jsx';

const NAV_PRIMARY = [
  { id: 'dashboard', icon: 'dashboard', key: 'nav.dashboard', shortcut: '⌘1' },
  { id: 'explore',   icon: 'compass',   key: 'nav.explore',   shortcut: '⌘2', badge: '128' },
  { id: 'library',   icon: 'library',   key: 'nav.library',   shortcut: '⌘3' },
  { id: 'bench',     icon: 'beaker',    key: 'nav.bench' },
  { id: 'projects',  icon: 'kanban',    key: 'nav.projects',  badge: '7' },
  { id: 'graph',     icon: 'graph',     key: 'nav.graph' },
  { id: 'wiki',      icon: 'book',      key: 'nav.wiki' },
  { id: 'questions', icon: 'question',  key: 'nav.questions', badge: '12' },
  { id: 'reports',   icon: 'chart',     key: 'nav.reports' },
  { id: 'assistant', icon: 'bot',       key: 'nav.assistant', dot: true },
];

const NAV_SECONDARY = [
  { id: 'integrations', icon: 'plug', key: 'nav.integrations' },
  { id: 'settings',     icon: 'more', key: 'nav.settings' },
];

const PROJECTS = [
  { id: 'rack',   color: '#8896FF', labelKo: '서버랙 디지털 트윈',  labelEn: 'Server-rack digital twin', count: 42 },
  { id: 'nav',    color: '#6AD7F0', labelKo: '로봇 내비게이션',     labelEn: 'Robotic navigation',        count: 31 },
  { id: '4d',     color: '#B49BFF', labelKo: '4D 재구성',           labelEn: '4D reconstruction',         count: 28 },
  { id: 'vla',    color: '#E8B86A', labelKo: 'VLA 임바디먼트',      labelEn: 'VLA embodiment',            count: 19 },
  { id: 'llm',    color: '#6BD8A7', labelKo: 'LLM 효율화',          labelEn: 'LLM efficiency',            count: 11 },
];

// Navigation context — set by app root in SPA mode.
const NavContext = React.createContext({ navigate: null });

function Sidebar({ active = 'dashboard', compact = false, onNavigate }) {
  const { t, lang } = useT();
  const navCtx = React.useContext(NavContext);
  const auth = useAuth();
  // Prefer explicit prop, then context, then no-op
  const handleNav = onNavigate || navCtx.navigate || (() => {});
  // collapsed state — shared across screens via localStorage
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('pp-sidebar-collapsed') || 'false'); } catch { return false; }
  });
  // Listen for cross-screen toggle (other Sidebar instances may exist on canvas)
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'pp-sidebar-collapsed') setCollapsed(JSON.parse(e.newValue || 'false'));
    };
    window.addEventListener('storage', onStorage);
    const onPing = () => {
      try { setCollapsed(JSON.parse(localStorage.getItem('pp-sidebar-collapsed') || 'false')); } catch {}
    };
    window.addEventListener('pp-sidebar-toggle', onPing);
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('pp-sidebar-toggle', onPing); };
  }, []);
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('pp-sidebar-collapsed', JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('pp-sidebar-toggle'));
  };
  // "compact" prop forces collapsed look (reader screen)
  const isCollapsed = collapsed || compact;
  return (
    <aside className={'pp-sidebar' + (isCollapsed ? ' is-collapsed' : '')}>
      {/* brand */}
      <div className="pp-brand">
        <button className="pp-brand-mark" onClick={toggleCollapsed} title={lang === 'ko' ? '사이드바 접기/펴기' : 'Toggle sidebar'}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 20 12 4l8 16" stroke="#8896FF" strokeWidth="1.6" strokeLinejoin="round"/>
            <path d="M7.5 13.5h9" stroke="#B49BFF" strokeWidth="1.6" strokeLinecap="round"/>
            <circle cx="12" cy="4" r="1.6" fill="#8896FF"/>
          </svg>
        </button>
        {!isCollapsed && (
          <div className="col grow" style={{ lineHeight: 1.1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.01em' }}>PaperPilot</div>
            <div style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 2 }}>{t('brand.tagline')}</div>
          </div>
        )}
        {!isCollapsed && (
          <button className="pp-sidebar-toggle" onClick={toggleCollapsed} title={lang === 'ko' ? '접기' : 'Collapse'}>
            <I name="chevronLeft" size={13}/>
          </button>
        )}
      </div>

      {/* ⌘K palette trigger */}
      <button
        className="pp-cmd-trigger"
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true }))}
        title={lang === 'ko' ? '커맨드 팔레트' : 'Command palette'}
      >
        <I name="search" size={14}/>
        {!isCollapsed && <span className="pp-cmd-trigger-text">{lang === 'ko' ? '이동, 검색, 명령…' : 'Jump, search, command…'}</span>}
        {!isCollapsed && <span className="pp-kbd">⌘K</span>}
      </button>

      <div className="pp-nav-section">
        {!isCollapsed && <div className="pp-nav-label">{t('nav.section.workspace')}</div>}
        {NAV_PRIMARY.map(item => (
          <div key={item.id}
            className={'pp-nav-item' + (active === item.id ? ' is-active' : '')}
            title={isCollapsed ? t(item.key) : undefined}
            onClick={() => handleNav(item.id)}>
            <I name={item.icon} size={16} />
            {!isCollapsed && <span className="pp-nav-text">{t(item.key)}</span>}
            {!isCollapsed && item.badge && <span className="pp-nav-badge">{item.badge}</span>}
            {!isCollapsed && item.dot && <span className="pp-nav-dot" />}
            {!isCollapsed && item.shortcut && active !== item.id && <span className="pp-nav-shortcut">{item.shortcut}</span>}
            {isCollapsed && item.dot && <span className="pp-nav-dot pp-nav-dot-corner" />}
          </div>
        ))}
      </div>

      <div className="pp-nav-section">
        {!isCollapsed && (
          <div className="pp-nav-label row" style={{ justifyContent: 'space-between' }}>
            <span>{t('nav.section.projects')}</span>
            <I name="plus" size={11} />
          </div>
        )}
        {PROJECTS.map(p => (
          <div key={p.id} className="pp-project-item"
            title={isCollapsed ? (lang === 'ko' ? p.labelKo : p.labelEn) : undefined}
            onClick={() => handleNav('projects')}>
            <span className="pp-project-dot" style={{ background: p.color, boxShadow: `0 0 8px ${p.color}66` }} />
            {!isCollapsed && <span className="pp-nav-text ellipsis">{lang === 'ko' ? p.labelKo : p.labelEn}</span>}
            {!isCollapsed && <span className="pp-project-count">{p.count}</span>}
          </div>
        ))}
      </div>

      <div className="pp-nav-section pp-nav-bottom">
        {NAV_SECONDARY.map(item => (
          <div key={item.id}
            className={'pp-nav-item' + (active === item.id ? ' is-active' : '')}
            title={isCollapsed ? t(item.key) : undefined}
            onClick={() => handleNav(item.id)}>
            <I name={item.icon} size={16} />
            {!isCollapsed && <span className="pp-nav-text">{t(item.key)}</span>}
          </div>
        ))}

        {auth.user ? (
          <div className="pp-user" title={auth.user.email}>
            {auth.user.picture
              ? <img className="pp-user-avatar" src={auth.user.picture} alt="" referrerPolicy="no-referrer"/>
              : <div className="pp-user-avatar">{(auth.user.name || auth.user.email || 'U').slice(0, 1).toUpperCase()}</div>}
            {!isCollapsed && (
              <>
                <div className="col grow" style={{ minWidth: 0 }}>
                  <div className="ellipsis" style={{ fontSize: 12.5, fontWeight: 500 }}>{auth.user.name || auth.user.email}</div>
                  <div className="ellipsis" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{auth.user.email}</div>
                </div>
                <button
                  className="iconbtn"
                  title={lang === 'ko' ? '로그아웃' : 'Sign out'}
                  onClick={auth.signOut}
                >
                  <I name="more" size={14}/>
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            className="pp-user"
            onClick={auth.signIn}
            disabled={auth.status === 'loading'}
            title={lang === 'ko' ? 'Google 로그인' : 'Sign in with Google'}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
          >
            <div className="pp-user-avatar">?</div>
            {!isCollapsed && (
              <div className="col grow" style={{ minWidth: 0 }}>
                <div className="ellipsis" style={{ fontSize: 12.5, fontWeight: 500 }}>{lang === 'ko' ? '로그인' : 'Sign in'}</div>
                <div className="ellipsis" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{lang === 'ko' ? 'Google 계정으로' : 'with Google'}</div>
              </div>
            )}
          </button>
        )}
      </div>
    </aside>
  );
}

function TopBar({ search, actions = true, breadcrumbs = null, kbd = '⌘K' }) {
  const { t } = useT();
  if (!search) search = t('cmd.placeholder');
  return (
    <header className="pp-topbar">
      <div className="pp-topbar-left">
        {breadcrumbs}
      </div>

      <div className="pp-cmd">
        <I name="search" size={15} />
        <input className="pp-cmd-input" placeholder={search} />
        <div className="pp-cmd-mode">
          <span className="pp-cmd-mode-pill">{t('cmd.mode')}</span>
        </div>
        <span className="pp-kbd">{kbd}</span>
      </div>

      {actions && (
        <div className="pp-topbar-actions">
          <button className="btn sm ghost"><I name="plus" size={13}/> {t('cmd.import')}</button>
          <button className="iconbtn" title="Notifications">
            <I name="bell" size={16}/>
            <span className="pp-notif-dot"/>
          </button>
          <button className="iconbtn" title="AI assistant"><I name="sparkles" size={16} style={{ color: 'var(--acc-indigo)' }}/></button>
          <div className="pp-divider-v"/>
          <div className="pp-status">
            <span className="pp-status-dot"/>
            <span>{t('cmd.synced')}</span>
          </div>
        </div>
      )}
    </header>
  );
}

function Breadcrumb({ items }) {
  return (
    <div className="pp-crumb">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <I name="chevronRight" size={12} style={{ color: 'var(--fg-4)' }}/>}
          <span className={i === items.length - 1 ? 'pp-crumb-current' : 'pp-crumb-link'}>{it}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// Shell styles (injected once)
if (typeof document !== 'undefined' && !document.getElementById('pp-shell-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-shell-styles';
  s.textContent = `
  .pp-sidebar {
    width: 232px; flex-shrink: 0; height: 100%;
    background: var(--bg-1);
    border-right: 1px solid var(--border-1);
    display: flex; flex-direction: column;
    padding: 14px 10px 10px;
    gap: 14px;
    overflow: hidden;
    transition: width .18s cubic-bezier(0.2,0.7,0.3,1);
  }
  .pp-sidebar.is-collapsed { width: 58px; padding: 14px 8px 10px; align-items: center; }
  .pp-sidebar.is-collapsed .pp-brand { gap: 0; padding: 4px 0 8px; }
  .pp-sidebar.is-collapsed .pp-nav-item,
  .pp-sidebar.is-collapsed .pp-project-item,
  .pp-sidebar.is-collapsed .pp-user {
    justify-content: center;
    padding: 7px 6px;
  }
  .pp-sidebar.is-collapsed .pp-cmd-trigger { width: 38px; padding: 0; justify-content: center; }
  .pp-sidebar.is-collapsed .pp-user-avatar { margin: 0; }
  .pp-nav-dot-corner { position: absolute; top: 4px; right: 4px; }
  .pp-sidebar-toggle {
    display: flex; align-items: center; justify-content: center;
    width: 22px; height: 22px;
    border-radius: 5px;
    color: var(--fg-3);
    margin-left: auto;
    cursor: pointer;
    transition: background .12s, color .12s;
  }
  .pp-sidebar-toggle:hover { background: rgba(255,255,255,0.05); color: var(--fg-0); }
  .pp-brand { display: flex; align-items: center; gap: 10px; padding: 4px 8px 8px; }
  .pp-brand-mark {
    width: 30px; height: 30px; border-radius: 8px;
    background: linear-gradient(180deg, rgba(136,150,255,0.18), rgba(136,150,255,0.04));
    border: 1px solid rgba(136,150,255,0.30);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 16px rgba(136,150,255,0.20), inset 0 1px 0 rgba(255,255,255,0.08);
    cursor: pointer; padding: 0; flex-shrink: 0;
    transition: box-shadow .15s, transform .12s;
  }
  .pp-brand-mark:hover { box-shadow: 0 0 24px rgba(136,150,255,0.40), inset 0 1px 0 rgba(255,255,255,0.10); }
  .pp-brand-mark:active { transform: scale(0.96); }

  /* Command palette trigger button in sidebar */
  .pp-cmd-trigger {
    display: flex; align-items: center; gap: 8px;
    height: 32px; padding: 0 10px;
    border-radius: 7px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    color: var(--fg-2);
    cursor: pointer;
    text-align: left;
    font: inherit;
    transition: border-color .12s, background .12s;
  }
  .pp-cmd-trigger:hover { border-color: rgba(136,150,255,0.40); background: var(--bg-3); }
  .pp-cmd-trigger-text { flex: 1; font-size: 11.5px; color: var(--fg-3); }
  .pp-nav-section { display: flex; flex-direction: column; gap: 2px; }
  .pp-nav-label {
    font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--fg-3); padding: 6px 10px 4px;
  }
  .pp-nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 6px 10px;
    border-radius: 6px;
    color: var(--fg-1);
    font-size: 12.5px; font-weight: 450;
    cursor: pointer;
    position: relative;
  }
  .pp-nav-item:hover { background: rgba(255,255,255,0.03); color: var(--fg-0); }
  .pp-nav-item.is-active {
    background: linear-gradient(90deg, rgba(136,150,255,0.16), rgba(136,150,255,0.04));
    color: var(--fg-0);
    box-shadow: inset 2px 0 0 var(--acc-indigo);
  }
  .pp-nav-item.is-active::after {
    content: ''; position: absolute; right: -1px; top: 8px; bottom: 8px;
    width: 6px; background: radial-gradient(circle at left, rgba(136,150,255,0.4), transparent 70%);
  }
  .pp-nav-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pp-nav-badge {
    font-family: var(--font-mono); font-size: 10px;
    color: var(--fg-2); background: rgba(255,255,255,0.05);
    padding: 1px 6px; border-radius: 999px;
  }
  .pp-nav-dot {
    width: 6px; height: 6px; border-radius: 999px;
    background: var(--acc-indigo);
    box-shadow: 0 0 8px var(--acc-indigo);
  }
  .pp-nav-shortcut {
    font-family: var(--font-mono); font-size: 10px;
    color: var(--fg-4);
  }
  .pp-project-item {
    display: flex; align-items: center; gap: 10px;
    padding: 5px 10px;
    border-radius: 6px;
    color: var(--fg-1);
    font-size: 12px;
    cursor: pointer;
  }
  .pp-project-item:hover { background: rgba(255,255,255,0.03); }
  .pp-project-dot { width: 7px; height: 7px; border-radius: 2px; flex-shrink: 0; }
  .pp-project-count {
    font-family: var(--font-mono); font-size: 10px; color: var(--fg-3);
  }
  .pp-nav-bottom { margin-top: auto; }
  .pp-user {
    display: flex; align-items: center; gap: 10px;
    padding: 8px;
    margin-top: 8px;
    border-radius: 8px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
  }
  .pp-user-avatar {
    width: 28px; height: 28px; border-radius: 7px;
    background: linear-gradient(135deg, #5B68E0, #B49BFF);
    color: #fff; font-size: 11px; font-weight: 600;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.10) inset;
  }

  .pp-topbar {
    height: 52px;
    background: var(--bg-1);
    border-bottom: 1px solid var(--border-1);
    display: flex; align-items: center;
    padding: 0 16px;
    gap: 16px;
    position: relative;
    z-index: 5;
  }
  .pp-topbar-left { flex: 0 0 auto; min-width: 0; }
  .pp-topbar-actions { flex: 0 0 auto; display: flex; align-items: center; gap: 6px; }
  .pp-cmd {
    flex: 1 1 auto;
    max-width: 560px;
    height: 32px; padding: 0 10px;
    display: flex; align-items: center; gap: 8px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    border-radius: 8px;
    color: var(--fg-2);
    transition: border-color .12s;
  }
  .pp-cmd:focus-within { border-color: rgba(136,150,255,0.45); box-shadow: 0 0 0 3px rgba(136,150,255,0.12); }
  .pp-cmd-input { flex: 1; font-size: 12.5px; color: var(--fg-0); }
  .pp-cmd-input::placeholder { color: var(--fg-3); }
  .pp-cmd-mode-pill {
    font-family: var(--font-mono); font-size: 10px;
    padding: 2px 6px; border-radius: 4px;
    background: rgba(136,150,255,0.10); color: var(--acc-indigo);
    border: 1px solid rgba(136,150,255,0.25);
    letter-spacing: 0.02em;
  }
  .pp-kbd {
    font-family: var(--font-mono); font-size: 10.5px; color: var(--fg-3);
    padding: 2px 5px; border-radius: 4px;
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border-1);
  }
  .pp-notif-dot {
    position: absolute; width: 6px; height: 6px; border-radius: 999px;
    background: var(--acc-violet); top: 7px; right: 7px;
    box-shadow: 0 0 6px var(--acc-violet);
  }
  .pp-status {
    display: flex; align-items: center; gap: 6px;
    font-size: 11px; color: var(--fg-3); font-family: var(--font-mono);
  }
  .pp-status-dot {
    width: 6px; height: 6px; border-radius: 999px;
    background: var(--ok); box-shadow: 0 0 6px var(--ok);
  }
  .pp-divider-v { width: 1px; height: 18px; background: var(--border-1); margin: 0 4px; }

  .pp-crumb {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; color: var(--fg-2);
  }
  .pp-crumb-link { color: var(--fg-2); }
  .pp-crumb-current { color: var(--fg-0); font-weight: 500; }

  /* the canonical screen frame */
  .pp-screen {
    width: 100%; height: 100%;
    display: flex;
    background: var(--bg-0);
    color: var(--fg-0);
    position: relative;
    overflow: hidden;
  }
  .pp-main {
    flex: 1; min-width: 0;
    display: flex; flex-direction: column;
    position: relative;
  }
  .pp-content {
    flex: 1; min-height: 0;
    display: flex;
    overflow: hidden;
  }
  `;
  document.head.appendChild(s);
}

export { Sidebar, TopBar, Breadcrumb, NAV_PRIMARY, PROJECTS, NavContext };
